import React, { useEffect, useState, useCallback } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import api from '../api';

const STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];

const AdminOrders = () => {
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('all');
  const [selected, setSelected]     = useState(null);
  const [trackingInput, setTrackingInput] = useState({});
  const [forwarding, setForwarding] = useState({});

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get('/api/admin/orders');
    setOrders(data.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusChange = async (orderId, status) => {
    try {
      await api.put(`/api/admin/orders/${orderId}/status`, { status });
      setOrders(orders.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    await api.delete(`/api/admin/orders/${id}`);
    setOrders(orders.filter(o => o.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const handleExport = () => {
    window.open('http://localhost:5001/api/admin/orders/export', '_blank');
  };

  const handleTrackingSave = async (orderId) => {
    const tracking = trackingInput[orderId] ?? '';
    try {
      await api.put(`/api/admin/orders/${orderId}/tracking`, { tracking_number: tracking });
      setOrders(orders.map(o => o.id === orderId ? { ...o, tracking_number: tracking } : o));
      if (selected?.id === orderId) setSelected(s => ({ ...s, tracking_number: tracking }));
      alert('Tracking mis à jour. Email envoyé au client.');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur mise à jour tracking.');
    }
  };

  const handleForward = async (orderId) => {
    if (!window.confirm('Envoyer les détails de cette commande par email pour commander chez AliExpress ?')) return;
    setForwarding(f => ({ ...f, [orderId]: true }));
    try {
      await api.post(`/api/admin/orders/${orderId}/forward`);
      setOrders(orders.map(o => o.id === orderId ? { ...o, forwarded_to_supplier: 1 } : o));
      if (selected?.id === orderId) setSelected(s => ({ ...s, forwarded_to_supplier: 1 }));
      alert('Email envoyé ! Vérifiez votre boîte mail pour passer la commande chez AliExpress.');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur forwarding.');
    } finally {
      setForwarding(f => ({ ...f, [orderId]: false }));
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const totalProfit = orders.filter(o => o.status !== 'cancelled').reduce((s, o) => s + parseFloat(o.profit || 0), 0);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-main">
        <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1>Orders</h1>
            <p style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <span>{orders.length} commandes</span>
              <span style={{ color: '#10b981', fontWeight: 700 }}>Profit total : {totalProfit.toFixed(2)} MAD</span>
            </p>
          </div>
          <button className="btn btn-accent" onClick={handleExport}>📥 Export Excel</button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {['all', ...STATUSES].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-outline'}`}
              style={{ textTransform: 'capitalize' }}
            >
              {s} {s === 'all' ? `(${orders.length})` : `(${orders.filter(o => o.status === s).length})`}
            </button>
          ))}
        </div>

        <div className="card">
          {loading ? (
            <div className="loader"><div className="spinner" /></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th><th>Client</th><th>Produit</th><th>Qté</th><th>Total</th><th>Profit</th><th>Statut</th><th>Tracking</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>No orders found.</td></tr>
                  ) : filtered.map(o => (
                    <tr key={o.id}>
                      <td>
                        <div>#{o.id}</div>
                        <div style={{ fontSize: 10, color: '#aaa' }}>{new Date(o.created_at).toLocaleDateString()}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{o.customer_name}</div>
                        <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{o.customer_phone}</div>
                      </td>
                      <td>
                        <div>{o.product_name}</div>
                        {o.supplier_name && <div style={{ fontSize: 11, color: '#888' }}>🏭 {o.supplier_name}</div>}
                      </td>
                      <td>{o.quantity}</td>
                      <td>{parseFloat(o.total_price).toFixed(2)} MAD</td>
                      <td style={{ fontWeight: 700, color: parseFloat(o.profit) > 0 ? '#10b981' : '#ef4444' }}>
                        {o.profit != null ? `${parseFloat(o.profit).toFixed(2)} MAD` : '—'}
                      </td>
                      <td>
                        <select
                          value={o.status}
                          onChange={e => handleStatusChange(o.id, e.target.value)}
                          className="form-control"
                          style={{ padding: '.3rem .5rem', fontSize: '.82rem', width: 'auto' }}
                        >
                          {STATUSES.map(s => (
                            <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input
                            className="form-control"
                            style={{ padding: '.25rem .4rem', fontSize: '.8rem', width: 110 }}
                            placeholder="Numéro suivi"
                            value={trackingInput[o.id] !== undefined ? trackingInput[o.id] : (o.tracking_number || '')}
                            onChange={e => setTrackingInput(t => ({ ...t, [o.id]: e.target.value }))}
                          />
                          <button
                            className="btn btn-sm"
                            style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 13 }}
                            onClick={() => handleTrackingSave(o.id)}
                            title="Enregistrer le tracking">
                            ✓
                          </button>
                        </div>
                        {o.tracking_number && <div style={{ fontSize: 10, color: '#10b981', marginTop: 2 }}>✅ {o.tracking_number}</div>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setSelected(o)}>Voir</button>
                          <button
                            className="btn btn-sm"
                            style={{ background: o.forwarded_to_supplier ? '#d1fae5' : '#fff3cd', color: o.forwarded_to_supplier ? '#065f46' : '#856404', border: '1px solid', borderColor: o.forwarded_to_supplier ? '#10b981' : '#ffc107', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}
                            onClick={() => !o.forwarded_to_supplier && handleForward(o.id)}
                            disabled={forwarding[o.id]}
                            title={o.forwarded_to_supplier ? 'Déjà transmis au fournisseur' : 'Transmettre au fournisseur'}>
                            {forwarding[o.id] ? '...' : o.forwarded_to_supplier ? '✅ Transmis' : '📤 Fournisseur'}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(o.id)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Order detail modal */}
        {selected && (
          <div className="modal-overlay" onClick={() => setSelected(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Order #{selected.id}</h2>
                <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div className="card" style={{ padding: '1rem', background: '#f8f7f4' }}>
                    <strong style={{ fontSize: '.8rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</strong>
                    <p style={{ marginTop: '.5rem' }}><strong>{selected.customer_name}</strong></p>
                    <p style={{ fontSize: '.9rem' }}>{selected.customer_email}</p>
                    <p style={{ fontSize: '.9rem' }}>{selected.customer_phone}</p>
                    <p style={{ fontSize: '.9rem', marginTop: '.5rem' }}>{selected.customer_address}</p>
                    {selected.notes && <p style={{ marginTop: '.5rem', fontStyle: 'italic', fontSize: '.85rem' }}>Note: {selected.notes}</p>}
                  </div>
                  <div className="card" style={{ padding: '1rem', background: '#f8f7f4' }}>
                    <strong style={{ fontSize: '.8rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Commande</strong>
                    <p style={{ marginTop: '.5rem' }}>Produit: <strong>{selected.product_name}</strong></p>
                    <p>Quantité: {selected.quantity}</p>
                    <p>Total: <strong>{parseFloat(selected.total_price).toFixed(2)} MAD</strong></p>
                    {selected.profit != null && (
                      <p>Profit: <strong style={{ color: parseFloat(selected.profit) > 0 ? '#10b981' : '#ef4444' }}>{parseFloat(selected.profit).toFixed(2)} MAD</strong></p>
                    )}
                    <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginTop: '.5rem' }}>
                      {new Date(selected.created_at).toLocaleString()}
                    </p>
                  </div>
                  {/* Dropshipping */}
                  <div className="card" style={{ padding: '1rem', background: '#f0fdf4', border: '1px solid #d1fae5' }}>
                    <strong style={{ fontSize: '.8rem', color: '#065f46', textTransform: 'uppercase' }}>🏭 Dropshipping</strong>
                    {selected.supplier_name && <p style={{ marginTop: '.5rem' }}>Fournisseur: <strong>{selected.supplier_name}</strong></p>}
                    {selected.supplier_url && (
                      <p><a href={selected.supplier_url} target="_blank" rel="noopener noreferrer" style={{ color: '#e53935', fontWeight: 700 }}>🔗 Voir sur AliExpress</a></p>
                    )}
                    <div style={{ marginTop: '.75rem' }}>
                      <label style={{ fontSize: 12, fontWeight: 600 }}>Numéro de suivi</label>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <input
                          className="form-control"
                          style={{ flex: 1 }}
                          placeholder="Ex: LX123456789CN"
                          value={trackingInput[selected.id] !== undefined ? trackingInput[selected.id] : (selected.tracking_number || '')}
                          onChange={e => setTrackingInput(t => ({ ...t, [selected.id]: e.target.value }))}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleTrackingSave(selected.id)}>
                          Enregistrer
                        </button>
                      </div>
                      {selected.tracking_number && (
                        <p style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>✅ Tracking actuel: {selected.tracking_number}</p>
                      )}
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        className="btn btn-sm"
                        style={{ background: selected.forwarded_to_supplier ? '#d1fae5' : '#fef3c7', color: selected.forwarded_to_supplier ? '#065f46' : '#92400e', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 700, width: '100%' }}
                        onClick={() => !selected.forwarded_to_supplier && handleForward(selected.id)}
                        disabled={selected.forwarded_to_supplier || forwarding[selected.id]}>
                        {selected.forwarded_to_supplier ? '✅ Commande déjà transmise au fournisseur' : '📤 Transmettre commande au fournisseur (email AliExpress)'}
                      </button>
                      {selected.forwarded_at && <p style={{ fontSize: 11, color: '#aaa', marginTop: 4, textAlign: 'center' }}>Transmis le {new Date(selected.forwarded_at).toLocaleString()}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminOrders;
